/**
 * Encryption Utilities for PII/PHI Data
 *
 * HIPAA Compliance: All SSN and DOB data is encrypted using AES-256-GCM
 * before being stored in Firestore.
 *
 * Security Notes:
 * - Uses Web Crypto API (native browser encryption)
 * - AES-256-GCM provides authenticated encryption
 * - Unique IV (initialization vector) for each encryption
 * - Auth tag ensures data integrity
 * - Encryption key stored in Google Secret Manager (production)
 */

import { logger } from './logger';

// Convert string to ArrayBuffer
function str2ab(str: string): Uint8Array {
  const encoder = new TextEncoder();
  return encoder.encode(str);
}

// Convert ArrayBuffer to string
function ab2str(buffer: ArrayBuffer): string {
  const decoder = new TextDecoder();
  return decoder.decode(buffer);
}

// Convert ArrayBuffer to base64
function ab2base64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Convert base64 to ArrayBuffer
function base64ToAb(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Derive a CryptoKey from the encryption key string
 */
async function getEncryptionKey(keyString: string): Promise<CryptoKey> {
  // In production, the key string should come from environment variables
  // which are populated from Google Secret Manager
  const keyData = str2ab(keyString);

  return await globalThis.crypto.subtle.importKey(
    'raw',
    keyData.buffer as ArrayBuffer,
    { name: 'AES-GCM' } as AesKeyAlgorithm,
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt plaintext using AES-256-GCM
 *
 * @param plaintext - The data to encrypt (e.g., SSN, DOB)
 * @param keyString - The encryption key (from environment variables)
 * @returns Base64-encoded encrypted data with IV and auth tag
 */
export async function encrypt(plaintext: string, keyString: string): Promise<string> {
  try {
    // Generate random IV (12 bytes recommended for GCM)
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));

    // Get the encryption key
    const key = await getEncryptionKey(keyString);

    // Encrypt the plaintext
    const plainData = str2ab(plaintext);
    const encrypted = await globalThis.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: 128 // 128-bit authentication tag
      },
      key,
      plainData as BufferSource
    );

    // Combine IV + encrypted data
    // Format: [IV (12 bytes)][Encrypted Data + Auth Tag]
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encrypted), iv.length);

    // Return as base64
    return ab2base64(combined.buffer);
  } catch (error: unknown) {
    logger.error('Encryption error', error);
    throw new Error('Failed to encrypt data');
  }
}

/**
 * Decrypt ciphertext using AES-256-GCM
 *
 * @param ciphertext - Base64-encoded encrypted data with IV
 * @param keyString - The encryption key (from environment variables)
 * @returns Decrypted plaintext
 */
export async function decrypt(ciphertext: string, keyString: string): Promise<string> {
  try {
    // Decode from base64
    const combinedBuffer = base64ToAb(ciphertext);
    const combined = new Uint8Array(combinedBuffer);

    // Extract IV (first 12 bytes)
    const iv = combined.slice(0, 12);

    // Extract encrypted data (remaining bytes)
    const encrypted = combined.slice(12);

    // Get the encryption key
    const key = await getEncryptionKey(keyString);

    // Decrypt
    const decrypted = await globalThis.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: 128
      },
      key,
      encrypted
    );

    return ab2str(decrypted);
  } catch (error: unknown) {
    logger.error('Decryption error', error);
    throw new Error('Failed to decrypt data');
  }
}

/**
 * Mask SSN for display
 * @param ssn - Full SSN (XXX-XX-XXXX)
 * @returns Masked SSN (***-**-1234)
 */
export function maskSSN(ssn: string): string {
  if (!ssn || ssn.length < 4) {
    return '***-**-****';
  }

  // If the input is already a masked SSN, normalize the format and return last 4.
  if (isValidMaskedSSN(ssn)) {
    const last4 = ssn.replace(/\D/g, '').slice(-4);
    return `***-**-${last4}`;
  }

  // Remove any non-digit characters
  const digits = ssn.replace(/\D/g, '');

  if (digits.length !== 9) {
    return '***-**-****';
  }

  // Show only last 4 digits
  return `***-**-${digits.slice(-4)}`;
}

/**
 * Mask date of birth for display
 * @param dob - Full DOB (MM/DD/YYYY)
 * @param showYear - Whether to show the year
 * @returns Masked DOB (XX/XX/YYYY or XX/XX/XXXX)
 */
export function maskDOB(dob: string, showYear: boolean = true): string {
  if (!dob) {
    return '**/**/****';
  }

  const parts = dob.split('/');

  if (parts.length !== 3) {
    return '**/**/****';
  }

  if (showYear) {
    return `**/**/${parts[2]}`;
  } else {
    return '**/**/****';
  }
}

/**
 * Format SSN with dashes
 * @param ssn - SSN without formatting
 * @returns Formatted SSN (XXX-XX-XXXX)
 */
export function formatSSN(ssn: string): string {
  // Masked SSN: normalize to canonical "***-**-NNNN" form.
  if (isValidMaskedSSN(ssn)) {
    const last4 = ssn.replace(/\D/g, '').slice(-4);
    return `***-**-${last4}`;
  }

  // Remove all non-digit characters
  const digits = ssn.replace(/\D/g, '');

  if (digits.length !== 9) {
    return ssn; // Return original if invalid
  }

  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

/**
 * Match a masked SSN where only the last 4 digits are visible.
 * Accepts `***-**-NNNN`, `XXX-XX-NNNN`, `xxx-xx-NNNN`, with or without dashes.
 */
const MASKED_SSN_PATTERN = /^[*xX]{3}-?[*xX]{2}-?\d{4}$/;

/**
 * Returns true when the SSN string is a masked SSN that exposes only the last 4 digits.
 * Firms sometimes redact the first 5 digits on signed copies, so we accept those too.
 */
export function isValidMaskedSSN(ssn: string): boolean {
  if (!ssn) return false;
  return MASKED_SSN_PATTERN.test(ssn.trim());
}

/**
 * Normalize a user-entered or AI-extracted date string to canonical MM/DD/YYYY.
 *
 * Handles common slop produced by OCR or hand-typing:
 *  - "9/28/1954"  → "09/28/1954"  (zero-pad single-digit month/day)
 *  - "1/10/24"    → "01/10/2024"  (expand 2-digit year using 50-pivot)
 *  - "9/28/54"    → "09/28/1954"
 *  - "01-10-2024" → "01/10/2024"  (dash separator)
 *  - "2024-01-10" → "01/10/2024"  (ISO)
 *  - "01/10/2024" → "01/10/2024"  (already canonical)
 *
 * Returns the input unchanged when it doesn't match a recognized form, so partial
 * entries-in-progress (e.g. user has typed "01/" so far) don't get mangled.
 *
 * 2-digit years use a 50-year pivot: YY ≤ 49 → 20YY, otherwise 19YY. This works
 * well for both DOBs (typically pre-2000) and signed/accident dates (post-2000).
 */
export function normalizeDateInput(value: string): string {
  if (!value) return value;
  const trimmed = value.trim();

  // ISO: YYYY-MM-DD or YYYY/MM/DD
  const iso = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(trimmed);
  if (iso) {
    const [, y, m, d] = iso;
    return `${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y}`;
  }

  // M/D/YY or M/D/YYYY (also accepts dash separator)
  const mdy = /^(\d{1,2})[-/](\d{1,2})[-/](\d{2}|\d{4})$/.exec(trimmed);
  if (mdy) {
    const [, m, d, yRaw] = mdy;
    let y = yRaw;
    if (y.length === 2) {
      const yyNum = parseInt(y, 10);
      const fullYear = yyNum <= 49 ? 2000 + yyNum : 1900 + yyNum;
      y = String(fullYear);
    }
    return `${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y}`;
  }

  return trimmed;
}

/** Extract the last 4 digits from either a full or masked SSN. Returns null if fewer than 4 digits are present. */
export function ssnLast4(ssn: string): string | null {
  if (!ssn) return null;
  const digits = ssn.replace(/\D/g, '');
  if (digits.length < 4) return null;
  return digits.slice(-4);
}

/**
 * Validate SSN format. Accepts either a full 9-digit SSN OR a masked SSN where only
 * the last 4 digits are visible (e.g. `***-**-3948`). Use `isValidFullSSN` when you
 * need to ensure all 9 digits are known.
 * @param ssn - SSN to validate
 * @returns true if valid
 */
export function isValidSSN(ssn: string): boolean {
  if (isValidMaskedSSN(ssn)) {
    return true;
  }
  return isValidFullSSN(ssn);
}

/**
 * Validate that a string is a full 9-digit SSN (no masking allowed).
 */
export function isValidFullSSN(ssn: string): boolean {
  const digits = ssn.replace(/\D/g, '');

  // Must be exactly 9 digits
  if (digits.length !== 9) {
    return false;
  }

  // Cannot be all zeros or have area number 000
  if (digits === '000000000' || digits.startsWith('000')) {
    return false;
  }

  // Cannot have group number 00
  if (digits.slice(3, 5) === '00') {
    return false;
  }

  // Cannot have serial number 0000
  if (digits.slice(5) === '0000') {
    return false;
  }

  return true;
}

/**
 * Validate date of birth
 * @param dob - DOB string (MM/DD/YYYY)
 * @returns true if valid
 */
export function isValidDOB(dob: string): boolean {
  const parts = dob.split('/');

  if (parts.length !== 3) {
    return false;
  }

  const month = parseInt(parts[0], 10);
  const day = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);

  // Validate month
  if (month < 1 || month > 12) {
    return false;
  }

  // Validate day
  if (day < 1 || day > 31) {
    return false;
  }

  // Validate year (reasonable range)
  const currentYear = new Date().getFullYear();
  if (year < 1900 || year > currentYear) {
    return false;
  }

  // Validate actual date
  const date = new Date(year, month - 1, day);
  if (date.getMonth() + 1 !== month || date.getDate() !== day) {
    return false;
  }

  return true;
}

/**
 * Generate a secure random token for approval URLs
 * @param length - Length of the token (default 32)
 * @returns Random hex string
 */
export function generateApprovalToken(length: number = 32): string {
  const array = new Uint8Array(length);
  globalThis.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Hash a value for comparison (e.g., for searching encrypted data)
 * Note: This is one-way hashing, not encryption
 */
export async function hashValue(value: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', data);
  return ab2base64(hashBuffer);
}
